import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { applyUpdates } from 'src/utils/object';
import { reqUpdatePreferences } from 'src/api/user';
import { getSelectedThemeId } from 'src/services/theme';
import { getAvailableFontIds, getSelectedFontId } from 'src/services/font';
import type { AppFontId } from 'src/services/font';

import { DarkModes } from 'src/constants/app';
import {
  DeclarationTypes,
  CustomsStatusDeltaT,
  CustomsStatusDeltaI,
  CustomsStatusDeltaE,
  DeclarationStates
} from 'src/constants/declarations';

function createDefault<T extends Record<string, string>>(
  statusEnum: T
): { events: Record<T[keyof T], { enabled: boolean; pdf: boolean }> } {
  const events = {} as Record<T[keyof T], { enabled: boolean; pdf: boolean }>;
  for (const status of Object.values(statusEnum)) {
    if (status === 'bae' || status === 'validated') {
      events[status as T[keyof T]] = { enabled: true, pdf: true };
    } else {
      events[status as T[keyof T]] = { enabled: false, pdf: false };
    }
  }
  events[DeclarationStates.IN_ERROR as T[keyof T]] = { enabled: false, pdf: false };
  return { events };
}

class NomenclatureSettings {
  orderSimilarityDefault: boolean = false;
  displaySections: boolean = false;
  displaySearchRank: boolean = false;
  enableSearchHighlight: boolean = true;
  defaultRitaTerritory: string = '';
  showCET: boolean = true;
  showCITES: boolean = true;
  showREACH: boolean = true;
  showFGAS: boolean = true;
  showAD: boolean = true;
  showCBAM: boolean = true;
  showDUG: boolean = true;
  showEUTR: boolean = true;
  advancedSearch: boolean = false;
  showMEUR: boolean = true;
  showCONT: boolean = true;

  constructor(init?: Partial<NomenclatureSettings>) {
    if (init) {
      Object.assign(this, init);
    }
  }
}

class DeclarationSettings {
  declarationAdvancedSearch: string[] = [];
  receiveDeclarationMail: boolean = true;
  linkAllArticlesToContainers: boolean = true;
  declarationListMode: boolean = false;
  receiveDeclarationErrorsByMail: boolean = false;
  displayBellNotifications: boolean = true;
  defaultAgency: string = '';
  numberOfMonthsToLoad: number = 3;
  defaultDeclarationSubType!: DeclarationTypes;
  defaultDeclarationType!: DeclarationTypes;

  constructor(init?: Partial<DeclarationSettings>) {
    if (init) {
      Object.assign(this, init);
    }
  }
}

class InterfaceSettings {
  // TODO: Pinia. This is not ideal, because if the user has a browser in 'en' but 'fr' language set in preferences, we will download en for nothing
  lang: string = navigator.language.substring(0, 2);
  darkMode: DarkModes = DarkModes.BROWSER;
  lastVersionSeen: string = '';
  theme: string = getSelectedThemeId();
  font: AppFontId = getSelectedFontId();

  constructor(init?: Partial<InterfaceSettings>) {
    if (init) {
      Object.assign(this, init);
    } else {
      this.theme = getSelectedThemeId();
      this.font = getSelectedFontId();
    }
  }
}

class RegulatoryMonitoringSettings {
  eurLexFollowedKeyWords: string[] = [];
  followedCustomsBaseEvents: string[] = [];

  constructor(init?: Partial<RegulatoryMonitoringSettings>) {
    if (init) {
      Object.assign(this, init);
    }
  }
}

class NotificationSettings {
  delta_t: { events: Record<CustomsStatusDeltaT, { enabled: boolean; pdf: boolean }> } =
    createDefault(CustomsStatusDeltaT);
  delta_i: { events: Record<CustomsStatusDeltaI, { enabled: boolean; pdf: boolean }> } =
    createDefault(CustomsStatusDeltaI);
  delta_e: { events: Record<CustomsStatusDeltaE, { enabled: boolean; pdf: boolean }> } =
    createDefault(CustomsStatusDeltaE);

  constructor(init?: Partial<NotificationSettings>) {
    if (init) {
      Object.assign(this, init);
    }
  }
}

class CustomsBaseSettings {
  autoSaveMode: boolean = false;

  constructor(init?: Partial<CustomsBaseSettings>) {
    if (init) {
      Object.assign(this, init);
    }
  }
}

class ClassificationSettings {
  displayImgs: boolean = true;

  constructor(init?: Partial<ClassificationSettings>) {
    if (init) {
      Object.assign(this, init);
    }
  }
}

class MeasuresSettings {
  compactMode: boolean = true;

  constructor(init?: Partial<MeasuresSettings>) {
    if (init) {
      Object.assign(this, init);
    }
  }
}

export interface UserSettings {
  nomenclature: NomenclatureSettings;
  declaration: DeclarationSettings;
  interface: InterfaceSettings;
  regulatoryMonitoring: RegulatoryMonitoringSettings;
  customsBaseSettings: CustomsBaseSettings;
  classification: ClassificationSettings;
  measures: MeasuresSettings;
  lastSelectedMeasuresTerritory: string;
  notifications: NotificationSettings;
}

function createDefaultUserSettings(): UserSettings {
  return {
    nomenclature: new NomenclatureSettings(),
    declaration: new DeclarationSettings(),
    interface: new InterfaceSettings(),
    regulatoryMonitoring: new RegulatoryMonitoringSettings(),
    customsBaseSettings: new CustomsBaseSettings(),
    classification: new ClassificationSettings(),
    measures: new MeasuresSettings(),
    notifications: new NotificationSettings(),
    lastSelectedMeasuresTerritory: ''
  };
}

export const usePreferencesStore = defineStore('preferences', () => {
  const defaults = createDefaultUserSettings();
  const nomenclature = ref(defaults.nomenclature);
  const declaration = ref(defaults.declaration);
  const interfaceSettings = ref(defaults.interface);
  const regulatoryMonitoring = ref(defaults.regulatoryMonitoring);
  const customsBaseSettings = ref(defaults.customsBaseSettings);
  const classification = ref(defaults.classification);
  const measures = ref(defaults.measures);
  const notifications = ref(defaults.notifications);
  const lastSelectedMeasuresTerritory = ref(defaults.lastSelectedMeasuresTerritory);

  const isDarkMode = computed(() => {
    if (interfaceSettings.value.darkMode === DarkModes.DARK) {
      return true;
    }
    if (
      interfaceSettings.value.darkMode === DarkModes.BROWSER &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      return true;
    }
    return false;
  });

  const lastDeclarationType = computed(() => {
    const validDeclarationTypes = [
      DeclarationTypes.DELTA_G,
      DeclarationTypes.DELTA_I,
      DeclarationTypes.DELTA_E,
      DeclarationTypes.DELTA_T
    ];
    const storedDeclarationType = localStorage.getItem('lastDeclarationType');
    return storedDeclarationType && validDeclarationTypes.includes(storedDeclarationType as DeclarationTypes)
      ? (storedDeclarationType as DeclarationTypes)
      : null;
  });

  function getPersistedState(): UserSettings {
    return {
      nomenclature: nomenclature.value,
      declaration: declaration.value,
      interface: interfaceSettings.value,
      regulatoryMonitoring: regulatoryMonitoring.value,
      customsBaseSettings: customsBaseSettings.value,
      classification: classification.value,
      measures: measures.value,
      notifications: notifications.value,
      lastSelectedMeasuresTerritory: lastSelectedMeasuresTerritory.value
    };
  }

  function resetPreferences() {
    const nextDefaults = createDefaultUserSettings();
    nomenclature.value = nextDefaults.nomenclature;
    declaration.value = nextDefaults.declaration;
    interfaceSettings.value = nextDefaults.interface;
    regulatoryMonitoring.value = nextDefaults.regulatoryMonitoring;
    customsBaseSettings.value = nextDefaults.customsBaseSettings;
    classification.value = nextDefaults.classification;
    measures.value = nextDefaults.measures;
    notifications.value = nextDefaults.notifications;
    lastSelectedMeasuresTerritory.value = nextDefaults.lastSelectedMeasuresTerritory;
  }

  async function savePreferences() {
    await reqUpdatePreferences(getPersistedState());
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function initPreferences(preferences: any) {
    if (preferences) {
      applyUpdates(getPersistedState() as unknown as Record<string, unknown>, preferences);
      if (typeof preferences.display_imgs === 'boolean') {
        classification.value.displayImgs = preferences.display_imgs;
      }
    }
    if (!interfaceSettings.value.theme || interfaceSettings.value.theme.trim() === '') {
      interfaceSettings.value.theme = getSelectedThemeId();
    }
    if (!interfaceSettings.value.font || !getAvailableFontIds().includes(interfaceSettings.value.font)) {
      interfaceSettings.value.font = getSelectedFontId();
    }
  }

  return {
    nomenclature,
    declaration,
    // Keep public name `interface` (reserved word in some contexts but valid as property)
    interface: interfaceSettings,
    regulatoryMonitoring,
    customsBaseSettings,
    classification,
    measures,
    notifications,
    lastSelectedMeasuresTerritory,
    isDarkMode,
    lastDeclarationType,
    resetPreferences,
    savePreferences,
    initPreferences
  };
});
